import Cookies from 'js-cookie';
import { stringify } from 'query-string';

import { Optional } from './types';
import { delay } from './async';

type DjangoRestFrameworkSerializerErrorData<T> = {
  [K in keyof T]?: T[K] extends Optional<any[]>
    ? string[] // arrays are technically objects, so let's handle those first to handle like most primitives
    : T[K] extends Optional<object> // objects can be nested so let's get recursive
    ? DjangoRestFrameworkSerializerErrorData<T[K]>
    : string[];
};

// Custom error to handle errors from the server
export class FetchError<T = object> extends Error {
  constructor(
    public status: number = 500,
    public code: string = 'bad_request',
    public detail: string = 'Error making request',
    public data: DjangoRestFrameworkSerializerErrorData<T> = {}
  ) {
    super(JSON.stringify(data));

    this.status = status; // Status of the response. Ex: 400/404/500
    this.code = code; // Django Rest exception code. Ex: not_authenticated
    this.detail = detail; // Django Rest exceptions details. Ex: Authentication credentials were not provided.
    this.data = data; // Django Rest serialized errors. Ex: { username: ['No user found'] }
  }
}

export interface IFetchConfig {
  url: string;
  method?: 'GET' | 'PUT' | 'PATCH' | 'POST' | 'DELETE';
  body?: { [key: string]: any };
}

interface IFetchOptions {
  method: string;
  headers: string[][];
  body?: string;
}

// TODO: Move to barnyard and abstract the common pieces between all fetches
export const fetcher = async <T>(config: IFetchConfig): Promise<T> => {
  const { url, method = 'GET', body } = config;
  let urlToFetch = url;

  // Set up the proper headers
  const options: IFetchOptions = {
    method,
    headers: [],
  };

  // Add the CSRF token and set as json type if not a GET request
  if (method !== 'GET') {
    options.headers = [
      ['X-CSRFToken', window.CSRF_TOKEN || Cookies.get('csrftoken') || ''],
      ['Content-Type', 'application/json'],
    ];
  }

  // JSON stringify the body or parameterize it
  if (body && method === 'GET') {
    urlToFetch += `?${stringify(body)}`;
  } else if (body) {
    options.body = JSON.stringify(body);
  }

  const response = await fetch(urlToFetch, options);

  // Protecting against times where we are returned a text/html request even though
  // we asked for JSON
  const contentType = response.headers.get('content-type');
  if (contentType && !contentType.includes('application/json')) {
    // Throw so react-query knows that we errored
    throw new FetchError(
      response.status,
      'server_error',
      'Trouble connecting to Vetcove. If this issue continues, please reach out to Vetcove via the icon in the bottom right corner.',
      {}
    );
  }

  // Get the JSON returned from the server, but don't try to parse empty content
  const json = response.status === 204 ? undefined : await response.json();

  if (!response.ok) {
    // Throw so react-query knows that we errored
    throw new FetchError(response.status, json?.code, json?.detail, json);
  }

  return json;
};

/**
 * Implements the same interface as fetcher but prints to the console instead
 * of actually sending an API request. Useful for mocking out API requests but
 * keeping the rest of the surrounding code close to what you would expect in
 * the finished iteration
 * @param config standard fetcher config defined in `fetcher`
 * @param genResponse produces the response that you want to return from this API call
 * @param delayMs the time to the simulated request should take, defaults to 1000ms
 * if an exception is thrown when this function is called it will consider the thrown value
 * to be an error response
 */
export const fakeFetcher = async <T>(
  { url, body, method }: IFetchConfig,
  genResponse: () => T,
  delayMs: number = 750
): Promise<T> => {
  // allow for a console log here since this is a dev only util
  // eslint-disable-next-line no-console
  console.log(
    `TODO: mocked API call would send a ${method ?? 'GET'} request ${
      body ? `with ${JSON.stringify(body)}` : ''
    } to "${url}"`
  );
  await delay(delayMs);
  return genResponse();
};
